// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import React from 'react';
import {
  LocalStorageContext,
  LocalStorageContextProps,
  LocalStorageStateProps,
} from './LocalStorageContext';

interface LocalStorageProviderProps {
  children: React.ReactNode;
}
export const LocalStorageProvider: React.FC<LocalStorageProviderProps> = ({ children }) => {
  const [localStorageState, setLocalStorage] = React.useState({} as LocalStorageStateProps);
  const getStateFromLocalStorage = () => {
    const pollingEnabled = localStorage.getItem('pollingEnabled');
    const webSocketEnabled = localStorage.getItem('webSocketEnabled');
    setLocalStorage((p) => ({
      ...p,
      pollingEnabled: pollingEnabled === null || pollingEnabled === 'true',
      webSocketEnabled: webSocketEnabled === null || webSocketEnabled === 'true',
    }));
  };
  const setLocalStorageState = React.useCallback((key: string, value: string) => {
    localStorage.setItem(key, value);
    if (key === 'pollingEnabled') {
      setLocalStorage((p) => ({
        ...p,
        pollingEnabled: value === 'true',
      }));
    }
    if (key === 'webSocketEnabled') {
      setLocalStorage((p) => ({
        ...p,
        webSocketEnabled: value === 'true',
      }));
    }
  }, []);
  const clearWsKeysFromLocalStorageState = React.useCallback(() => {
    // There are some key-value pairs we want to preserve upon clearing local storage.
    const pollingEnabled = localStorage.getItem('pollingEnabled');
    const webSocketEnabled = localStorage.getItem('webSocketEnabled');

    localStorage.clear();

    if (pollingEnabled !== null) {
      localStorage.setItem('pollingEnabled', pollingEnabled);
    }
    if (webSocketEnabled !== null) {
      localStorage.setItem('webSocketEnabled', webSocketEnabled);
    }

    setLocalStorage((p) => ({
      ...p,
      pollingEnabled: pollingEnabled === 'true',
      webSocketEnabled: webSocketEnabled === 'true',
    }));
  }, []);
  React.useEffect(() => {
    getStateFromLocalStorage();
  }, []);
  const value: LocalStorageContextProps = React.useMemo(
    () => ({
      localStorageState,
      setLocalStorageState,
      clearWsKeysFromLocalStorageState,
    }),
    [setLocalStorageState, localStorageState, clearWsKeysFromLocalStorageState],
  );
  return <LocalStorageContext.Provider value={value}>{children}</LocalStorageContext.Provider>;
};
